import React, { useEffect, useState } from "react";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useMultipleTokens } from "@/hooks/use-dexscreener";
import {
  formatPrice,
  formatPercentage,
  COMMON_TOKENS,
} from "@/lib/dexscreener";

type WalletData = {
  balances: Record<string, number>;
  transactions: Array<any>;
};

export default function Wallet() {
  const [user, setUser] = useState<any | null>(null);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch token prices from DexScreener
  const tokenAddresses = [
    COMMON_TOKENS.ethereum.USDT,
    COMMON_TOKENS.ethereum.USDC,
    COMMON_TOKENS.ethereum.WETH,
  ];
  const { pairs, loading: pricesLoading } = useMultipleTokens(tokenAddresses, {
    chainId: "ethereum",
    refetchInterval: 60000, // Refetch every 60 seconds
  });

  useEffect(() => {
    const userDataStr = sessionStorage.getItem("userData");
    const userEmail = sessionStorage.getItem("userEmail");

    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        setUser(userData);
        return;
      } catch {
        sessionStorage.removeItem("userData");
      }
    }

    const localUser = localStorage.getItem("user");
    if (localUser) {
      try {
        const userData = JSON.parse(localUser);
        setUser(userData);
        sessionStorage.setItem("userEmail", userData.email || userEmail || "");
        sessionStorage.setItem("userData", JSON.stringify(userData));
        return;
      } catch {
        localStorage.removeItem("user");
      }
    }

    const legacyUser = localStorage.getItem("currentUser");
    if (legacyUser) {
      try {
        const userData = JSON.parse(legacyUser);
        setUser(userData);
        sessionStorage.setItem("userEmail", userData.email || userEmail || "");
        sessionStorage.setItem("userData", JSON.stringify(userData));
        localStorage.removeItem("currentUser");
        return;
      } catch {
        localStorage.removeItem("currentUser");
      }
    }

    navigate("/auth");
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetchWallet();
  }, [user]);

  async function fetchWallet() {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/wallet/${user.id}`);
      let data;
      try {
        const text = await res.text();
        if (!text) {
          throw new Error("Empty response from server.");
        }
        // Check if response is HTML (error page)
        if (text.trim().startsWith("<")) {
          console.error(
            "Server returned HTML instead of JSON. Response:",
            text.substring(0, 200),
          );
          throw new Error("Server returned an error.");
        }
        data = JSON.parse(text);
      } catch (parseErr) {
        console.error("Failed to parse wallet response:", parseErr);
        if (parseErr instanceof Error && parseErr.message.includes("HTML")) {
          throw parseErr;
        }
        throw new Error("Server error: Invalid response format.");
      }
      if (!res.ok)
        throw new Error(
          data?.error || data?.message || "Failed to fetch wallet",
        );
      setWallet(data);
    } catch (err: any) {
      toast({
        title: "ERROR",
        description: err?.message || "UNABLE TO LOAD WALLET",
      });
    } finally {
      setLoading(false);
    }
  }

  // Get token price from DexScreener pairs
  function getTokenPrice(tokenSymbol: string): number {
    const pair = pairs.find(
      (p) =>
        p.baseToken.symbol === tokenSymbol ||
        p.quoteToken.symbol === tokenSymbol,
    );
    if (!pair) return 0;

    // If this is the quote token, use the price directly
    if (pair.quoteToken.symbol === tokenSymbol) {
      return 1; // Usually USDT/USDC, so 1
    }
    // If this is the base token, use priceUsd
    return parseFloat(pair.priceUsd || "0");
  }

  async function approveWithdraw(txId: string) {
    if (!user) return;
    try {
      const res = await fetch("/api/withdraw/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, txId }),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data?.error || data?.message || "Approve failed");
      // update wallet
      fetchWallet();
      toast({
        title: "WITHDRAWAL APPROVED",
        description: "FUNDS HAVE BEEN DEDUCTED FROM YOUR ACCOUNT",
      });
    } catch (err: any) {
      toast({
        title: "ERROR",
        description: err?.message || "UNABLE TO APPROVE WITHDRAWAL",
      });
    }
  }

  if (!user) return null;

  const totalBalance = wallet
    ? Object.entries(wallet.balances).reduce((sum, [tokenSymbol, balance]) => {
        const price = getTokenPrice(tokenSymbol);
        const usdValue = (balance as number) * price;
        return sum + usdValue;
      }, 0)
    : 0;

  return (
    <section className="container py-8 sm:py-16">
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        <Card className="bg-secondary">
          <CardHeader>
            <CardTitle>MY PORTFOLIO</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <div className="text-sm text-muted-foreground">BALANCE</div>
              <div className="mt-2 text-2xl font-bold">
                {formatPrice(totalBalance)}
              </div>
            </div>

            <div className="grid gap-4 mt-6">
              <div>
                <h5 className="text-xs font-semibold">ASSETS</h5>
                <div className="mt-2 grid gap-2">
                  {wallet ? (
                    Object.entries(wallet.balances).map(
                      ([tokenSymbol, balance]) => {
                        const price = getTokenPrice(tokenSymbol);
                        const usdValue = (balance as number) * price;
                        const tokenPair = pairs.find(
                          (p) =>
                            p.baseToken.symbol === tokenSymbol ||
                            p.quoteToken.symbol === tokenSymbol,
                        );
                        const change = tokenPair?.priceChange24h || 0;

                        return (
                          <div
                            key={tokenSymbol}
                            className="flex items-center justify-between rounded-md border p-3"
                          >
                            <div>
                              <div className="text-sm font-medium">
                                {tokenSymbol}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {(balance as number).toFixed(4)} •{" "}
                                {formatPrice(usdValue)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-mono text-sm">
                                {formatPrice(price)}
                              </div>
                              <div
                                className={`text-xs font-medium ${
                                  change >= 0
                                    ? "text-emerald-600"
                                    : "text-red-600"
                                }`}
                              >
                                {formatPercentage(change)}
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )
                  ) : (
                    <div className="text-muted-foreground">
                      LOADING BALANCES...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-2">
              <Button
                onClick={() => navigate("/deposit")}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                DEPOSIT
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/withdraw")}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                WITHDRAW
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-secondary">
          <CardHeader>
            <CardTitle>TRANSACTIONS</CardTitle>
            <CardDescription>RECENT ACTIVITY</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 border rounded text-muted-foreground">
              Upcoming transactions processing
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
